import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';

import type { SideNavItem } from '../types';

import { useRevealPresence } from '../../../../../utils/useRevealPresence';
import { Icon } from '../../../../Icon/Icon';
import { StatusBadge } from '../../../../StatusBadge/StatusBadge';
import { useSideNavContext } from '../context';
import { isItemActive, isLinkCurrent } from '../utils';
import { SideNavControl } from './SideNavControl';
import { SideNavSubItem } from './SideNavSubItem';

interface SideNavItemRowProps {
  item: SideNavItem;
}

/** The icon, on the AI tile when the entry is the brand mark. */
export const SideNavItemIcon = memo(function SideNavItemIcon({
  isActive,
  item,
}: {
  isActive: boolean;
  item: SideNavItem;
}) {
  if (item.iconVariant === 'ai') {
    return (
      <span className="eds-side-nav--ai-tile">
        <Icon icon={item.icon} size="s" />
      </span>
    );
  }
  return <Icon filled={isActive} icon={item.icon} size="s" />;
});

/** One entry of the expanded sidebar, with its nested links when it is a group. */
export const SideNavItemRow = memo(function SideNavItemRow({
  item,
}: SideNavItemRowProps) {
  const { activeKey, labels, onActivate, regionAttributes } =
    useSideNavContext();
  const children = item.children ?? [];
  const hasChildren = children.length > 0;
  const isActive = isItemActive(item, activeKey);
  const isOwnLinkActive = isLinkCurrent(item.key, activeKey);

  const [isExpanded, setIsExpanded] = useState(false);
  const childrenRef = useRef<HTMLDivElement>(null);
  const { isLeaving: isCollapsing, isRendered: areChildrenRendered } =
    useRevealPresence(isExpanded, childrenRef, '--_reveal-exit');

  useEffect(() => {
    if (isActive && hasChildren) {
      setIsExpanded(true);
    }
  }, [isActive, hasChildren]);

  return (
    <div className="eds-side-nav--item" {...item.groupDataAttributes}>
      <div
        className={clsx(
          'eds-side-nav--row',
          isOwnLinkActive && 'eds-side-nav--row--active',
        )}
      >
        <SideNavControl
          className={clsx(
            'eds-side-nav--item-control',
            isActive && 'eds-side-nav--item-control--active',
          )}
          isCurrent={isOwnLinkActive}
          link={item}
          onActivate={onActivate}
        >
          <SideNavItemIcon isActive={isActive} item={item} />
          <span className="eds-side-nav--item-label">{item.label}</span>
          {item.badge && (
            <span className="eds-side-nav--item-badge">
              <StatusBadge status="informational" text={item.badge} />
            </span>
          )}
        </SideNavControl>

        {hasChildren && (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? labels.collapseSection(item.label)
                : labels.expandSection(item.label)
            }
            className="eds-side-nav--chevron"
            {...regionAttributes.sectionToggle}
            {...item.toggleDataAttributes}
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded((previous) => !previous);
            }}
          >
            <Icon icon="chevron-down" size="s" />
          </button>
        )}
      </div>

      {hasChildren && areChildrenRendered && (
        <div
          ref={childrenRef}
          aria-hidden={isCollapsing}
          className={clsx(
            'eds-side-nav--reveal',
            isCollapsing && 'eds-side-nav--reveal--leaving',
          )}
        >
          <div className="eds-side-nav--reveal-content eds-side-nav--children">
            <div aria-hidden="true" className="eds-side-nav--children-line" />
            {children.map((child) => (
              <SideNavSubItem key={child.key} link={child} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
